(function () {
  'use strict';

  const PAYLOAD_VERSION = 2;
  const LEGACY_PAYLOAD_VERSION = 1;
  const SESSION_KEY = 'ls-shared-cart-payload';
  const MAX_LINE_ITEMS = 100;
  const MAX_QUANTITY = 999;
  const MAX_LINK_LENGTH = 24000;

  function route(root, path) {
    const normalizedRoot = root && root.endsWith('/') ? root : `${root || '/'}\/`;
    return `${normalizedRoot}${path.replace(/^\/+/, '')}`;
  }

  function bytesToBase64Url(bytes) {
    let binary = '';
    const chunkSize = 0x8000;

    for (let index = 0; index < bytes.length; index += chunkSize) {
      binary += String.fromCharCode.apply(null, bytes.subarray(index, index + chunkSize));
    }

    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  function base64UrlToBytes(value) {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
  }

  async function encodePayload(payload) {
    const source = new TextEncoder().encode(JSON.stringify(payload));

    if ('CompressionStream' in window) {
      const stream = new Blob([source]).stream().pipeThrough(new CompressionStream('gzip'));
      const compressed = new Uint8Array(await new Response(stream).arrayBuffer());
      return `g.${bytesToBase64Url(compressed)}`;
    }

    return `u.${bytesToBase64Url(source)}`;
  }

  async function decodePayload(token) {
    const separator = token.indexOf('.');
    if (separator < 1) throw new Error('The shared cart link is invalid.');

    const encoding = token.slice(0, separator);
    const bytes = base64UrlToBytes(token.slice(separator + 1));
    let decoded = bytes;

    if (encoding === 'g') {
      if (!('DecompressionStream' in window)) {
        throw new Error('This browser cannot open the compressed cart link.');
      }
      const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
      decoded = new Uint8Array(await new Response(stream).arrayBuffer());
    } else if (encoding !== 'u') {
      throw new Error('The shared cart link uses an unsupported format.');
    }

    return JSON.parse(new TextDecoder().decode(decoded));
  }

  function randomNonce() {
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      return bytesToBase64Url(window.crypto.getRandomValues(new Uint8Array(8)));
    }

    return Math.random().toString(36).slice(2, 13);
  }

  function publicProperties(properties) {
    return Object.fromEntries(
      Object.entries(properties || {})
        .filter(([key, value]) => !key.startsWith('_') && value !== null && value !== '')
        .map(([key, value]) => [key, String(value)])
    );
  }

  function cartItemToSharedItem(item) {
    const compactItem = [
      Number(item.id),
      item.handle || item.product_handle || '',
      Number(item.quantity)
    ];
    const properties = publicProperties(item.properties);
    const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id
      ? Number(item.selling_plan_allocation.selling_plan.id)
      : null;

    if (Object.keys(properties).length || sellingPlanId) compactItem[3] = properties;
    if (sellingPlanId) compactItem[4] = sellingPlanId;
    return compactItem;
  }

  function normalizePayload(payload) {
    if (!payload) {
      throw new Error('The shared cart link is invalid.');
    }

    if (payload.version === LEGACY_PAYLOAD_VERSION && Array.isArray(payload.items)) {
      return payload;
    }

    if (payload.v !== PAYLOAD_VERSION || !Array.isArray(payload.i)) {
      throw new Error('The shared cart link is invalid.');
    }

    return {
      version: PAYLOAD_VERSION,
      items: payload.i.map((item) => {
        if (!Array.isArray(item)) throw new Error('A product in this shared cart is invalid.');
        return {
          variantId: item[0],
          productHandle: item[1],
          quantity: item[2],
          publicProperties: item[3] || {},
          sellingPlanId: item[4] || null
        };
      })
    };
  }

  function validatePayload(payload) {
    const normalizedPayload = normalizePayload(payload);
    if (!normalizedPayload.items.length) throw new Error('This shared cart is empty.');
    if (normalizedPayload.items.length > MAX_LINE_ITEMS) throw new Error('This shared cart has too many items.');

    normalizedPayload.items.forEach((item) => {
      if (!Number.isSafeInteger(Number(item.variantId)) || Number(item.variantId) <= 0) {
        throw new Error('A product in this shared cart is invalid.');
      }
      if (!Number.isInteger(Number(item.quantity)) || Number(item.quantity) < 1 || Number(item.quantity) > MAX_QUANTITY) {
        throw new Error('A quantity in this shared cart is invalid.');
      }
      if (typeof item.productHandle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/i.test(item.productHandle)) {
        throw new Error('A product link in this shared cart is invalid.');
      }
      if (item.publicProperties && (typeof item.publicProperties !== 'object' || Array.isArray(item.publicProperties))) {
        throw new Error('A product option in this shared cart is invalid.');
      }
    });

    return normalizedPayload;
  }

  async function fetchJson(url, options) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      ...options
    });

    if (!response.ok) {
      let message = `Request failed (${response.status}).`;
      try {
        const error = await response.json();
        message = error.description || error.message || message;
      } catch (parseError) {
        // Keep the HTTP status message when Shopify does not return JSON.
      }
      throw new Error(message);
    }

    return response.json();
  }

  class LivingShapesCartShare extends HTMLElement {
    constructor() {
      super();
      this.trigger = this.querySelector('[data-ls-share-trigger]');
      this.status = this.querySelector('[data-ls-share-status]');
    }

    connectedCallback() {
      if (!this.trigger || this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.trigger.addEventListener('click', () => this.shareOnWhatsApp());
    }

    setStatus(message, isError) {
      this.status.textContent = message;
      this.status.classList.toggle('errors', Boolean(isError));
    }

    setLoading(isLoading) {
      this.trigger.disabled = isLoading;
      this.trigger.classList.toggle('btn--loading', isLoading);
      this.setAttribute('aria-busy', String(isLoading));
    }

    async buildUrl() {
      const root = this.dataset.rootUrl || '/';
      const cart = await fetchJson(route(root, 'cart.js'), { cache: 'no-store' });
      if (!cart.items || !cart.items.length) throw new Error('Your cart is empty.');

      const payload = {
        v: PAYLOAD_VERSION,
        n: randomNonce(),
        i: cart.items.map(cartItemToSharedItem)
      };
      validatePayload(payload);

      const token = await encodePayload(payload);
      const sharedUrl = new URL(this.dataset.pageUrl, window.location.origin);
      const currentUrl = new URL(window.location.href);
      const previewThemeId = currentUrl.searchParams.get('preview_theme_id')
        || this.dataset.previewThemeId;
      if (previewThemeId) sharedUrl.searchParams.set('preview_theme_id', previewThemeId);
      sharedUrl.hash = `sc=${token}`;

      if (sharedUrl.href.length > MAX_LINK_LENGTH) {
        throw new Error('This cart is too large to share as one link.');
      }

      return sharedUrl.href;
    }

    async shareOnWhatsApp() {
      const popup = window.open('about:blank', '_blank');
      if (!popup) {
        this.setStatus('Please allow pop-ups to share your cart on WhatsApp.', true);
        return;
      }

      popup.opener = null;
      this.setLoading(true);
      this.setStatus('', false);

      try {
        const generatedUrl = await this.buildUrl();
        const message = this.dataset.shareMessage || 'Take a look at my Living Shapes cart';
        popup.location.replace(`https://wa.me/?text=${encodeURIComponent(`${message} ${generatedUrl}`)}`);
      } catch (error) {
        popup.close();
        this.setStatus(error.message || 'Unable to create the shared cart link.', true);
      } finally {
        this.setLoading(false);
      }
    }
  }

  class LivingShapesSharedCart extends HTMLElement {
    constructor() {
      super();
      this.rootUrl = this.dataset.rootUrl || '/';
      this.currency = this.dataset.currency || 'INR';
      this.moneyFormat = this.dataset.moneyFormat || '';
      this.loading = this.querySelector('[data-ls-shared-loading]');
      this.error = this.querySelector('[data-ls-shared-error]');
      this.content = this.querySelector('[data-ls-shared-content]');
      this.itemsHolder = this.querySelector('[data-ls-shared-items]');
      this.subtotal = this.querySelector('[data-ls-shared-subtotal]');
      this.status = this.querySelector('[data-ls-shared-status]');
      this.addButton = this.querySelector('[data-ls-add-all]');
      this.resolvedItems = [];
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.addButton.addEventListener('click', () => this.addAll());
      this.load();
    }

    formatMoney(cents) {
      if (window.theme?.Currency?.formatMoney && this.moneyFormat) {
        return window.theme.Currency.formatMoney(cents, this.moneyFormat);
      }

      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: 2
      }).format(Number(cents) / 100);
    }

    showError(message) {
      this.loading.hidden = true;
      this.content.hidden = true;
      this.error.hidden = false;
      this.error.textContent = message;
    }

    setStatus(message, isError) {
      this.status.textContent = message;
      this.status.classList.toggle('errors', Boolean(isError));
    }

    getToken() {
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      return hash.get('sc') || sessionStorage.getItem(SESSION_KEY) || '';
    }

    async resolveItem(sharedItem, productCache) {
      try {
        if (!productCache.has(sharedItem.productHandle)) {
          productCache.set(
            sharedItem.productHandle,
            fetchJson(route(this.rootUrl, `products/${encodeURIComponent(sharedItem.productHandle)}.js`), {
              cache: 'no-store'
            })
          );
        }

        const product = await productCache.get(sharedItem.productHandle);
        const variant = product.variants.find(
          (candidate) => String(candidate.id) === String(sharedItem.variantId)
        );
        if (!variant) throw new Error('Variant not found');

        const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
        return {
          sharedItem,
          available: Boolean(variant.available),
          title: product.title || 'Unavailable product',
          variantTitle,
          image: variant.featured_image?.src || product.featured_image || '',
          price: Number(variant.price),
          productUrl: route(this.rootUrl, `products/${encodeURIComponent(sharedItem.productHandle)}`)
        };
      } catch (error) {
        return {
          sharedItem,
          available: false,
          title: 'Unavailable product',
          variantTitle: '',
          image: '',
          price: null,
          productUrl: route(this.rootUrl, `products/${encodeURIComponent(sharedItem.productHandle)}`)
        };
      }
    }

    renderProperties(holder, properties) {
      const entries = Object.entries(properties || {});
      if (!entries.length) return;

      const list = document.createElement('dl');
      list.className = 'ls-shared-cart__properties';
      entries.forEach(([key, value]) => {
        const term = document.createElement('dt');
        term.textContent = key;
        const description = document.createElement('dd');
        description.textContent = value;
        list.append(term, description);
      });
      holder.append(list);
    }

    renderItem(item) {
      const listItem = document.createElement('li');
      listItem.className = 'ls-shared-cart__item';

      const imageLink = document.createElement('a');
      imageLink.className = 'ls-shared-cart__image';
      imageLink.href = item.productUrl;
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.title;
        image.loading = 'lazy';
        image.width = 180;
        image.height = 180;
        imageLink.append(image);
      }

      const details = document.createElement('div');
      details.className = 'ls-shared-cart__details';
      const title = document.createElement('a');
      title.className = 'ls-shared-cart__title';
      title.href = item.productUrl;
      title.textContent = item.title;
      details.append(title);

      if (item.variantTitle) {
        const variant = document.createElement('p');
        variant.className = 'ls-shared-cart__variant';
        variant.textContent = item.variantTitle;
        details.append(variant);
      }
      this.renderProperties(details, item.sharedItem.publicProperties);

      const quantity = document.createElement('p');
      quantity.className = 'ls-shared-cart__quantity';
      quantity.textContent = `Quantity: ${item.sharedItem.quantity}`;
      details.append(quantity);

      const price = document.createElement('div');
      price.className = 'ls-shared-cart__price';
      if (item.available && Number.isFinite(item.price)) {
        const unitPrice = document.createElement('span');
        unitPrice.textContent = this.formatMoney(item.price);
        const linePrice = document.createElement('strong');
        linePrice.textContent = this.formatMoney(item.price * item.sharedItem.quantity);
        price.append(unitPrice, linePrice);
      } else {
        const unavailable = document.createElement('strong');
        unavailable.className = 'ls-shared-cart__unavailable';
        unavailable.textContent = 'Currently unavailable';
        price.append(unavailable);
      }

      listItem.append(imageLink, details, price);
      return listItem;
    }

    async load() {
      try {
        const token = this.getToken();
        if (!token) throw new Error('This shared cart link is missing its cart information.');

        const payload = validatePayload(await decodePayload(token));
        sessionStorage.setItem(SESSION_KEY, token);
        if (window.location.hash) {
          history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
        }

        const productCache = new Map();
        this.resolvedItems = await Promise.all(
          payload.items.map((item) => this.resolveItem(item, productCache))
        );

        const fragment = document.createDocumentFragment();
        this.resolvedItems.forEach((item) => fragment.append(this.renderItem(item)));
        this.itemsHolder.replaceChildren(fragment);

        const allAvailable = this.resolvedItems.every(
          (item) => item.available && Number.isFinite(item.price)
        );
        const subtotal = this.resolvedItems.reduce(
          (sum, item) => sum + (item.available && Number.isFinite(item.price)
            ? item.price * item.sharedItem.quantity
            : 0),
          0
        );

        this.subtotal.textContent = this.formatMoney(subtotal);
        this.addButton.disabled = !allAvailable;
        if (!allAvailable) {
          this.setStatus('One or more products are unavailable. All products must be available before this cart can be added.', true);
        }

        this.loading.hidden = true;
        this.error.hidden = true;
        this.content.hidden = false;
      } catch (error) {
        this.showError(error.message || 'Unable to open this shared cart.');
      }
    }

    cartLine(item, includePrivateProperties) {
      const properties = includePrivateProperties
        ? item.properties || {}
        : item.sharedItem.publicProperties || {};
      const sellingPlanId = includePrivateProperties
        ? item.selling_plan_allocation?.selling_plan?.id
        : item.sharedItem.sellingPlanId;
      const line = {
        id: Number(includePrivateProperties ? item.id : item.sharedItem.variantId),
        quantity: Number(includePrivateProperties ? item.quantity : item.sharedItem.quantity)
      };

      if (Object.keys(properties).length) line.properties = properties;
      if (sellingPlanId) line.selling_plan = Number(sellingPlanId);
      return line;
    }

    async clearCart() {
      return fetchJson(route(this.rootUrl, 'cart/clear.js'), {
        method: 'POST',
        body: JSON.stringify({})
      });
    }

    async addLines(lines) {
      return fetchJson(route(this.rootUrl, 'cart/add.js'), {
        method: 'POST',
        body: JSON.stringify({ items: lines })
      });
    }

    setBusy(isBusy) {
      this.addButton.disabled = isBusy || this.resolvedItems.some((item) => !item.available);
      this.addButton.classList.toggle('btn--loading', isBusy);
      this.setAttribute('aria-busy', String(isBusy));
    }

    async addAll() {
      if (!this.resolvedItems.length || this.resolvedItems.some((item) => !item.available)) return;
      this.setBusy(true);
      this.setStatus('', false);

      let originalLines = [];
      let clearedExistingCart = false;

      try {
        const currentCart = await fetchJson(route(this.rootUrl, 'cart.js'), { cache: 'no-store' });
        originalLines = (currentCart.items || []).map((item) => this.cartLine(item, true));

        if (originalLines.length) {
          const confirmed = window.confirm(
            'Adding this shared cart will replace the items currently in your cart. Continue?'
          );
          if (!confirmed) {
            this.setBusy(false);
            return;
          }
          await this.clearCart();
          clearedExistingCart = true;
        }

        const sharedLines = this.resolvedItems.map((item) => this.cartLine(item, false));
        await this.addLines(sharedLines);
        window.location.assign(route(this.rootUrl, 'cart'));
      } catch (error) {
        if (clearedExistingCart && originalLines.length) {
          try {
            await this.clearCart();
            await this.addLines(originalLines);
            this.setStatus('The shared cart could not be added. Your previous cart has been restored.', true);
          } catch (restoreError) {
            this.setStatus('The shared cart could not be added, and the previous cart could not be restored. Please review your cart.', true);
          }
        } else {
          this.setStatus(error.message || 'The shared cart could not be added.', true);
        }
        this.setBusy(false);
      }
    }
  }

  if (!customElements.get('ls-cart-share')) {
    customElements.define('ls-cart-share', LivingShapesCartShare);
  }
  if (!customElements.get('ls-shared-cart')) {
    customElements.define('ls-shared-cart', LivingShapesSharedCart);
  }
})();
